<template>
  <div class="cp-box-toggle">
    <div class="cp-box-toggle-text">
      <span class="cp-box-toggle-name">{{ label }}</span>
      <span v-if="hint" class="cp-box-toggle-hint">{{ hint }}</span>
    </div>
    <CustomSwitch :value="isOn" :disabled="disabled" @input="onInput" />
  </div>
</template>

<script>
import CustomSwitch from '../../parts/CustomSwitch.vue'

/**
 * One labeled show/hide switch for a single checkout box, bound to a key on the
 * profile's box_visibility map. Lives inside the box's own step tab; the step's
 * inline preview reflects the same value so the admin sees the box with/without it.
 */
export default {
  name: 'BoxVisibilityToggle',
  components: { CustomSwitch },
  props: {
    // The reactive box_visibility object on the profile (mutated in place).
    // Optional: in `controlled` mode the parent owns the value and we don't mutate it.
    boxVisibility: { type: Object, default: null },
    // Which box this toggles, e.g. 'custom_terms'.
    boxKey: { type: String, default: '' },
    label: { type: String, default: '' },
    hint: { type: String, default: '' },
    // Disable the switch (greyed, non-interactive) — e.g. a feature gated off globally.
    disabled: { type: Boolean, default: false },
    // Controlled mode: the parent supplies `value` and handles the change via @toggle,
    // instead of this component flipping boxVisibility[boxKey] itself. Used by the Stripe
    // row, which must open a modal / confirm before the state actually changes.
    controlled: { type: Boolean, default: false },
    value: { type: Boolean, default: false },
  },
  computed: {
    isOn() {
      return this.controlled ? !!this.value : !!(this.boxVisibility && this.boxVisibility[this.boxKey]);
    }
  },
  methods: {
    onInput(val) {
      if (this.disabled) return;
      if (this.controlled) {
        // Let the parent decide what happens (e.g. open the Stripe key modal).
        this.$emit('toggle', !!val);
        return;
      }
      // $set guarantees reactivity even if the key wasn't previously present.
      this.$set(this.boxVisibility, this.boxKey, !!val);
    }
  }
};
</script>

<style scoped>
.cp-box-toggle {
  display: flex; align-items: center; justify-content: space-between;
  gap: 1rem; padding: 0.75rem 1rem; margin-bottom: 1rem;
  background: #f7f9fc; border: 1px solid #e7ecf3; border-radius: 0.75rem;
}
.cp-box-toggle-text { display: flex; flex-direction: column; min-width: 0; }
.cp-box-toggle-name { font-size: 0.9rem; font-weight: 600; color: #343841; }
.cp-box-toggle-hint { font-size: 0.74rem; color: #8a93a2; margin-top: 2px; }
</style>
